import { Input, Table, message as antdMessage } from 'antd';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Field from '@fmbt/ui/Field';
import LoadingSpinner from '@fmbt/ui/LoadingSpinner';
import { deviceClient, samplePointClient } from 'clients';
import { DeviceWithRelations } from 'clients/device';
import { EM_DASH } from 'constants/index';
import { useAssetTypes } from 'store/modules/assetTypes/hooks';
import { GetSamplePointDto } from 'types/dto/sample-point.dto';
import { getAssetTypeName } from 'utils/SamplePoints/asset-types/get-asset-type-name';
import { alphabeticalSorter, numericalSorter } from 'utils/sorters';

const DEBOUNCE_MS = 300;

type DeviceBasedSamplePointSelectProps = {
  value?: string;
  onChange?: (value: number | string | undefined) => void;
};

function DeviceBasedSamplePointSelect({
  value,
  onChange
}: DeviceBasedSamplePointSelectProps): JSX.Element {
  const [search, setSearch] = useState('');
  const [device, setDevice] = useState<DeviceWithRelations | null>(null);
  const [loadingDevice, setLoadingDevice] = useState(false);

  const [samplePoints, setSamplePoints] = useState<GetSamplePointDto[]>([]);
  const [loadingSamplePoints, setLoadingSamplePoints] = useState(false);
  const assetTypes = useAssetTypes();

  // Use ref to avoid dependency chain issues with onChange
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const clearDevice = useCallback(() => {
    setDevice(null);
    setSamplePoints([]);
    onChangeRef.current?.(undefined);
  }, []);

  const fetchSamplePoints = useCallback(async (deviceId: number) => {
    setSamplePoints([]);
    setLoadingSamplePoints(true);
    const response = await samplePointClient.fetchSamplePointsByDeviceId(deviceId);
    setLoadingSamplePoints(false);

    if (response.error) {
      antdMessage.error(response.error.message || 'An error occurred while fetching sample points');
      return;
    }

    if (response.data && response.data.length > 0) {
      setSamplePoints(response.data);
    }
  }, []);

  const fetchDevice = useCallback(async (searchText: string) => {
    clearDevice();

    setLoadingDevice(true);
    const response = await deviceClient.fetchOneDeviceBySerialNumber(searchText);
    setLoadingDevice(false);

    if (response.error) {
      antdMessage.error(response.error.message || 'An error occurred while searching for devices');
      return;
    }

    if (response.data) {
      setDevice(response.data);
      fetchSamplePoints(response.data.id);
    }
  }, [clearDevice, fetchSamplePoints]);

  const debouncedFetchDevice = useMemo(
    () => debounce((searchText: string) => fetchDevice(searchText), DEBOUNCE_MS),
    [fetchDevice]
  );

  useEffect(() => {
    if (search.trim() === '') {
      return;
    }
    debouncedFetchDevice(search);
    return () => {
      debouncedFetchDevice.cancel();
    };
  }, [search, debouncedFetchDevice]);

  const handleSamplePointSelect = useCallback((samplePoint: GetSamplePointDto) => {
    onChangeRef.current?.(samplePoint.sid);
  }, []);

  const deviceColumns = [
    {
      title: 'Field',
      dataIndex: 'field',
      key: 'field',
      width: '40%'
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value'
    }
  ];

  const deviceTableData = device ? [
    { key: 'id', field: 'ID', value: device.id },
    { key: 'name', field: 'Name', value: device.name },
    { key: 'serialNumber', field: 'Serial Number', value: device.serialNumber },
    { key: 'enterprise', field: 'Enterprise', value: device.enterprise?.name || EM_DASH },
    { key: 'site', field: 'Site', value: device.site?.name || EM_DASH }
  ] : [];

  const samplePointColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: numericalSorter('id')
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: alphabeticalSorter('name')
    },
    {
      title: 'Asset Type',
      dataIndex: 'assetTypeId',
      key: 'assetTypeId',
      render: (_, record: GetSamplePointDto) =>
        getAssetTypeName(record, assetTypes) || EM_DASH,
      sorter: (a: GetSamplePointDto, b: GetSamplePointDto) =>
        (getAssetTypeName(a, assetTypes) || '')
          .localeCompare(getAssetTypeName(b, assetTypes) || '')
    },
    {
      title: 'Sid',
      dataIndex: 'sid',
      key: 'sid',
      sorter: alphabeticalSorter('sid')
    }
  ];

  const renderSamplePointsSection = () => {
    if (loadingSamplePoints) {
      return <LoadingSpinner size={16} />;
    }

    if (samplePoints.length === 0) {
      return <div className="body-muted-copy">No sample points found</div>;
    }

    return (
      <Field label="Sample Points">
        <Table
          columns={samplePointColumns}
          dataSource={samplePoints}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            showTotal: (total) => `Total ${total} sample points`
          }}
          size="small"
          rowKey="sid"
          rowSelection={{
            type: 'radio',
            selectedRowKeys: value ? [value] : [],
            onSelect: (record) => {
              handleSamplePointSelect(record);
            }
          }}
        />
      </Field>
    );
  };

  const renderContent = () => {
    if (loadingDevice) {
      return <LoadingSpinner size={16} />;
    }

    if (device) {
      return (
        <>
          <Table
            columns={deviceColumns}
            dataSource={deviceTableData}
            pagination={false}
            size="small"
            showHeader={false}
            className="mb-16"
          />
          {renderSamplePointsSection()}
        </>
      );
    }

    if (search.trim()) {
      return <div className="body-muted-copy">No data</div>;
    }

    return null;
  };

  return (
    <>
      <Input
        placeholder="Search by serial number"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-16"
      />
      {renderContent()}
    </>
  );
}

export default DeviceBasedSamplePointSelect;
